import React from "react";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { PublishButton, DiscardButton } from "./ActionButtons";
import {
  childVariants,
  accordionContainer,
  accordionWrapper,
  accordionItem,
} from "shared/utils";

/**
 * UnsavedChangesSection Component
 * Displays unsaved changes and provides publish/discard actions
 */
const UnsavedChangesSection = ({
  isChangesExpanded,
  setIsChangesExpanded,
  sectionsWithChanges,
  allSections,
  publishingState,
  discardingState,
  onPublish,
  onDiscard,
  onPublishAll,
  onDiscardAll,
}) => {
  return (
    <motion.div
      className="themes__section"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={childVariants}
      style={{
        background:
          "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
        padding: "0",
        gap: "0",
      }}
    >
      {/* Header with toggle */}
      <motion.div
        variants={childVariants}
        initial="hidden"
        animate="visible"
        onClick={() => setIsChangesExpanded(!isChangesExpanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "3rem",
          padding: "0.75rem",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          color: "var(--title-color)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.85rem",
            fontWeight: "600",
          }}
        >
          <i
            className={`bx bx-chevron-${isChangesExpanded ? "down" : "right"}`}
          ></i>
          <span>Unsaved Changes</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            background: "var(--title-color)",
            color: "var(--body-color)",
            padding: "0.25rem 0.5rem",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "600",
          }}
        >
          <span>{sectionsWithChanges.length}</span>
        </div>
      </motion.div>

      {/* Expanded content */}
      <AnimatePresence mode="wait">
        {isChangesExpanded && (
          <motion.div
            variants={accordionContainer}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ overflow: "hidden" }}
          >
            {/* Sections list */}
            <motion.div
              variants={accordionWrapper}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                gap: "0",
                display: "flex",
                flexDirection: "column",
                padding: "0.5rem",
              }}
            >
              {sectionsWithChanges.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    padding: "1rem",
                    color: "var(--text-color)",
                    fontSize: "0.75rem",
                    textAlign: "center",
                  }}
                >
                  <i className="bx bx-check-circle"></i>
                  <span>No unsaved changes</span>
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  <div style={{ width: "100%" }}>
                    {sectionsWithChanges.map((section) => (
                      <motion.div
                        className="glass__card"
                        key={section.id}
                        variants={accordionItem}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr auto",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "0.4rem 0.5rem",
                          background: "var(--body-color)",
                          borderRadius: "3px",
                          marginBottom: "0.25rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "0.4rem",
                            fontSize: "0.7rem",
                            minWidth: 0,
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              color: "var(--text-color)",
                            }}
                          >
                            {section.label}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.25rem",
                            flexShrink: 0,
                          }}
                        >
                          <PublishButton
                            section={section}
                            publishingState={publishingState}
                            onPublish={onPublish}
                          />
                          <DiscardButton
                            section={section}
                            discardingState={discardingState}
                            onDiscard={onDiscard}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </AnimatePresence>
              )}
            </motion.div>

            {/* Global Publish/Discard buttons */}
            {sectionsWithChanges.length > 1 && (
              <motion.div
                variants={accordionWrapper}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  gap: "0.5rem",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <button
                  onClick={onPublishAll}
                  disabled={publishingState.all === true}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.2rem",
                    padding: "0.25rem 0.35rem",
                    background:
                      publishingState.all === "published"
                        ? "#10b981"
                        : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "2px",
                    fontSize: "0.6rem",
                    fontWeight: "600",
                    cursor:
                      publishingState.all === true ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    opacity: publishingState.all === true ? 0.8 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (publishingState.all !== true) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {publishingState.all === true ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity }}
                        style={{ display: "flex" }}
                      >
                        <i className="bx bx-loader"></i>
                      </motion.div>
                      <span>Publishing All...</span>
                    </>
                  ) : publishingState.all === "published" ? (
                    <>
                      <i className="bx bx-check-double"></i>
                      <span>All Published</span>
                    </>
                  ) : (
                    <>
                      <i className="bx bx-check-double"></i>
                      <span>Publish All</span>
                    </>
                  )}
                </button>
                <button
                  onClick={onDiscardAll}
                  disabled={discardingState.all === true}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.2rem",
                    padding: "0.25rem 0.35rem",
                    background: "#f97316",
                    color: "white",
                    border: "none",
                    borderRadius: "2px",
                    fontSize: "0.6rem",
                    fontWeight: "600",
                    cursor:
                      discardingState.all === true ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    opacity: discardingState.all === true ? 0.8 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (discardingState.all !== true) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {discardingState.all === true ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity }}
                        style={{ display: "flex" }}
                      >
                        <i className="bx bx-loader"></i>
                      </motion.div>
                      <span>Discarding All...</span>
                    </>
                  ) : discardingState.all === "discarded" ? (
                    <>
                      <i className="bx bx-check-double"></i>
                      <span>All Discarded</span>
                    </>
                  ) : (
                    <>
                      <i className="bx bx-trash-alt"></i>
                      <span>Discard All</span>
                    </>
                  )}
                </button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default UnsavedChangesSection;
